// Admin API Helper - Session-based authentication için
// Tüm admin API çağrıları credentials: 'include' ile yapılmalı (cookie göndermek için)

const isDevelopment = process.env.NODE_ENV === 'development' || 
                     (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

const productionBase = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : ((typeof process !== 'undefined' && process.env.REACT_APP_BASE_URL) || '');
const BASE_URL = isDevelopment ? 'http://localhost:8000' : productionBase;

/**
 * admin_api.php çağrılarına otomatik credentials: 'include' ekleyen interceptor
 * Böylece legacy fetch kullanan admin sayfalarında da session cookie gönderilir.
 */
if (typeof window !== 'undefined' && !window.__adminFetchPatched) {
  const originalFetch = window.fetch;
  window.fetch = (input, init = {}) => {
    try {
      const url = typeof input === 'string' ? input : input?.url;
      const isAdminEndpoint =
        typeof url === 'string' &&
        (url.includes('/backend/admin_api.php') ||
         url.includes('://localhost:8000/backend/admin_api.php') ||
         /^https?:\/\/[^/]+\/backend\/admin_api\.php/.test(url));

      if (isAdminEndpoint) {
        const merged = {
          credentials: 'include',
          ...init,
          headers: {
            ...(init.headers || {})
          }
        };
        return originalFetch(input, merged);
      }
    } catch (_) {
      // ignore and fallback
    }
    return originalFetch(input, init);
  };
  window.__adminFetchPatched = true;
}

/**
 * Admin API çağrısı yapar (session-based authentication)
 * Tüm admin API çağrıları bu function üzerinden yapılmalı
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const adminApiFetch = async (url, options = {}) => {
  const defaultOptions = {
    credentials: 'include', // Cookie göndermek için (session-based auth)
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, mergedOptions);
    
    // 401 Unauthorized - Session expired veya login gerekli
    if (response.status === 401) {
      // Session expired, login sayfasına yönlendir
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/admin/login')) {
        // localStorage'ı temizle
        localStorage.removeItem('adminLoggedIn');
        localStorage.removeItem('adminUser');
        window.location.href = '/admin/login';
      }
      throw new Error('Authentication required. Please login.');
    }
    
    return response;
  } catch (error) {
    console.error('Admin API Error:', error);
    throw error;
  }
};

/**
 * Admin API GET request
 */
export const adminApiGet = async (url) => {
  return await adminApiFetch(url, { method: 'GET' });
};

/**
 * Admin API POST request
 */
export const adminApiPost = async (url, data) => {
  return await adminApiFetch(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

/**
 * Admin API PUT request
 */
export const adminApiPut = async (url, data) => {
  return await adminApiFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

/**
 * Admin API DELETE request
 */
export const adminApiDelete = async (url) => {
  return await adminApiFetch(url, { method: 'DELETE' });
};

/**
 * Admin Login
 */
export const adminLogin = async (email, password) => {
  const response = await adminApiFetch(`${BASE_URL}/backend/admin_api.php?action=adminLogin`, {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Login failed');
  }

  return await response.json();
};

/**
 * Admin Logout
 */
export const adminLogout = async () => {
  const response = await adminApiFetch(`${BASE_URL}/backend/admin_api.php?action=adminLogout`, {
    method: 'POST'
  });

  return await response.json();
};

/**
 * Check Admin Auth Status
 */
export const checkAdminAuth = async () => {
  try {
    const response = await adminApiFetch(`${BASE_URL}/backend/admin_api.php?action=checkAdminAuth`);
    
    if (!response.ok) {
      return { authenticated: false };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return { authenticated: false };
  }
};

